import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { escritorio, esRutaInterna } from '../../lib/desktop'
import { qk } from '../../lib/queryClient'
import type { Project } from '../../lib/types'
import { IconoAsistente } from '../ayuda/IconoAsistente'
import './tabs.css'

// Tabs de navegación estilo navegador DENTRO de la app (domain/tabs.md).
// Cada tab es navegación REAL: recuerda su propia ruta, no es un shortcut.
// La tab activa sigue al router (como la barra de direcciones): navegar por el
// sidebar/links actualiza el path y el título de la tab activa.

interface Tab {
  id: string
  path: string // pathname + search de la tab (lo que se navega al activarla)
  title: string // derivado de la ruta (labels en español)
}

interface TabsState {
  tabs: Tab[]
  activeId: string
}

const STORAGE_KEY = 'bee_tabs'

// Labels en español por ruta estática. Las rutas dinámicas (/p/:pid, /chat/:id)
// se resuelven aparte en deriveTitle.
const STATIC_LABELS: Record<string, string> = {
  '/': 'inicio',
  '/perfil': 'mi perfil',
  '/ayuda': 'ayuda',
  '/panorama': 'panorama',
  '/mis-tareas': 'mis tareas',
  '/chat': 'chat',
  '/celulas': 'células',
  '/directorio': 'directorio',
  '/papelera': 'papelera',
  '/admin': 'admin',
}

function newId(): string {
  // crypto.randomUUID no existe en contextos no seguros muy viejos → fallback tolerante.
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

// El título de un proyecto lo intentamos leer del cache de react-query (la página
// Proyecto ya lo cachea bajo qk.project(pid)); si aún no está, caemos a "proyecto".
function projectTitle(pid: string, qc: QueryClient): string {
  const data = qc.getQueryData<{ project: Project; etag: string }>(qk.project(pid))
  const name = data?.project?.name?.trim()
  return name ? name : 'proyecto'
}

function deriveTitle(pathname: string, qc: QueryClient): string {
  const label = STATIC_LABELS[pathname]
  if (label) return label
  if (pathname.startsWith('/chat/')) return 'chat'
  if (pathname.startsWith('/p/')) {
    const pid = pathname.split('/')[2] ?? ''
    return pid ? projectTitle(pid, qc) : 'proyecto'
  }
  return 'bee camp'
}

function pathnameOf(fullPath: string): string {
  const q = fullPath.indexOf('?')
  return q === -1 ? fullPath : fullPath.slice(0, q)
}

// Restaura el estado de sessionStorage con validación tolerante; null si no hay
// nada usable (entonces el componente crea una tab con la ruta actual).
function loadState(storageKey: string): TabsState | null {
  try {
    const raw = sessionStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const rawTabs = (parsed as { tabs?: unknown }).tabs
    if (!Array.isArray(rawTabs)) return null
    const tabs: Tab[] = rawTabs
      .filter(
        (t): t is Tab =>
          !!t &&
          typeof (t as Tab).id === 'string' &&
          esRutaInterna((t as Tab).path) &&
          typeof (t as Tab).title === 'string',
      )
      .map((t) => ({ id: t.id, path: t.path, title: t.title }))
    if (!tabs.length) return null
    const rawActive = (parsed as { activeId?: unknown }).activeId
    const activeId = tabs.some((t) => t.id === rawActive) ? (rawActive as string) : tabs[0]!.id
    return { tabs, activeId }
  } catch {
    return null
  }
}

export function TabBar({ cuenta }: { cuenta: string }) {
  const storageKey = `${STORAGE_KEY}:${cuenta}`
  const ventanaMac = escritorio()?.platform === 'darwin'
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()

  // Estado inicial: restaura de sessionStorage o crea UNA tab con la ruta actual.
  const [state, setState] = useState<TabsState>(() => {
    const restored = loadState(storageKey)
    if (restored) return restored
    const id = newId()
    const path = location.pathname + location.search
    return { tabs: [{ id, path, title: deriveTitle(location.pathname, qc) }], activeId: id }
  })
  const { tabs, activeId } = state

  // Refs con el valor fresco para usarlos dentro de efectos que NO deben re-dispararse
  // por cambios de tab (solo por navegación del router / cache).
  const stateRef = useRef(state)
  stateRef.current = state
  // Marca que el próximo cambio de location lo provocamos NOSOTROS (cambiar/abrir/cerrar
  // tab) y por tanto NO debe re-derivar la tab activa desde la URL.
  const programmatic = useRef(false)

  const fullPath = location.pathname + location.search

  // Persistencia en sessionStorage en cada cambio.
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state))
    } catch {
      // sessionStorage puede fallar (modo privado / cuota) — las tabs siguen vivas en memoria.
    }
  }, [state, storageKey])

  // La tab ACTIVA sigue al router: cada navegación real (sidebar/links/back-forward)
  // actualiza su path y título, como la barra de direcciones de un navegador.
  // Solo depende de location: el activeId se lee por ref para no confundir un cambio
  // de tab con una navegación.
  useEffect(() => {
    if (programmatic.current) {
      programmatic.current = false
      return
    }
    const path = location.pathname + location.search
    const title = deriveTitle(location.pathname, qc)
    setState((s) => {
      const active = s.tabs.find((t) => t.id === s.activeId)
      if (active && active.path === path && active.title === title) return s
      return {
        ...s,
        tabs: s.tabs.map((t) => (t.id === s.activeId ? { ...t, path, title } : t)),
      }
    })
  }, [location.pathname, location.search, qc])

  // El nombre del proyecto puede llegar DESPUÉS (la query aún cargaba al navegar).
  // Nos suscribimos al cache y refrescamos el título de la tab activa si es un /p/:pid.
  // El cache avisa DURANTE el render de otro componente (montar un useQuery con una
  // clave nueva construye la entrada en pleno render), así que el setState se hace un
  // microtask después: si no, React lo canta en rojo ("Cannot update a component while
  // rendering a different component"). El título llega igual de rápido a la vista.
  useEffect(() => {
    let vivo = true
    const unsub = qc.getQueryCache().subscribe(() => {
      queueMicrotask(() => {
        if (!vivo) return
        const { tabs: cur, activeId: curActive } = stateRef.current
        const active = cur.find((t) => t.id === curActive)
        if (!active || !active.path.startsWith('/p/')) return
        const fresh = deriveTitle(pathnameOf(active.path), qc)
        if (fresh === active.title) return
        setState((s) => ({
          ...s,
          tabs: s.tabs.map((t) => (t.id === curActive ? { ...t, title: fresh } : t)),
        }))
      })
    })
    return () => {
      vivo = false
      unsub()
    }
  }, [qc])

  // Cambiar de tab → navegar a su ruta guardada (como un navegador).
  const selectTab = useCallback(
    (id: string) => {
      const target = tabs.find((t) => t.id === id)
      if (!target || id === activeId) return
      setState((s) => ({ ...s, activeId: id }))
      if (target.path !== fullPath) {
        programmatic.current = true
        navigate(target.path)
      }
    },
    [tabs, activeId, fullPath, navigate],
  )

  // "+" → tab nueva en "/" y la activa.
  const addTab = useCallback(() => {
    const id = newId()
    const dest = '/'
    setState((s) => ({
      tabs: [...s.tabs, { id, path: dest, title: deriveTitle(dest, qc) }],
      activeId: id,
    }))
    if (dest !== fullPath) {
      programmatic.current = true
      navigate(dest)
    }
  }, [fullPath, navigate, qc])

  // Cerrar (✕): la ÚLTIMA tab no se puede cerrar; si cerramos la activa, activa la
  // vecina (la de la derecha, o la anterior si era la última) y navega a su ruta.
  const closeTab = useCallback(
    (id: string, e?: React.MouseEvent) => {
      e?.stopPropagation()
      if (tabs.length <= 1) return
      const idx = tabs.findIndex((t) => t.id === id)
      if (idx < 0) return
      const remaining = tabs.filter((t) => t.id !== id)
      let nextActive = activeId
      if (id === activeId) {
        // remaining tiene >= 1 elemento (solo cerramos si había > 1 tab).
        const neighbor = remaining[Math.min(idx, remaining.length - 1)]!
        nextActive = neighbor.id
        if (neighbor.path !== fullPath) {
          programmatic.current = true
          navigate(neighbor.path)
        }
      }
      setState({ tabs: remaining, activeId: nextActive })
    },
    [tabs, activeId, fullPath, navigate],
  )

  useEffect(() => {
    const atajo = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 't') return
      e.preventDefault()
      if (!e.repeat) addTab()
    }
    window.addEventListener('keydown', atajo)
    return () => window.removeEventListener('keydown', atajo)
  }, [addTab])

  const single = tabs.length <= 1

  return (
    <div className={`tabbar${single ? ' tabbar-single' : ''}${ventanaMac ? ' tabbar-window' : ''}`} aria-label="Barra de pestañas">
      {(!single || ventanaMac) && (
        <div className="tabbar-list" role="tablist" aria-label="Pestañas">
          {tabs.map((t) => (
            <div
              key={t.id}
              role="tab"
              tabIndex={t.id === activeId ? 0 : -1}
              aria-selected={t.id === activeId}
              className={`tab${t.id === activeId ? ' tab-active' : ''}`}
              title={t.title}
              onClick={() => selectTab(t.id)}
              onKeyDown={(e) => {
                if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                  e.preventDefault()
                  const indice = tabs.findIndex((x) => x.id === t.id)
                  const siguiente = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (indice + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
                  const controles = e.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="tab"]')
                  controles?.[siguiente]?.focus()
                  selectTab(tabs[siguiente]!.id)
                }
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  selectTab(t.id)
                }
              }}
              // Botón central del ratón cierra la tab (gesto de navegador).
              onAuxClick={(e) => {
                if (e.button === 1) closeTab(t.id, e)
              }}
            >
              <span className="tab-title">{t.title}</span>
              <button
                type="button"
                className="tab-close"
                aria-label={`Cerrar ${t.title}`}
                title={`Cerrar ${t.title}`}
                disabled={single}
                onClick={(e) => closeTab(t.id, e)}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
      {ventanaMac && <><div className="tabbar-drag" aria-hidden="true" /><button type="button" className="tabbar-action" aria-label="Ir a Inicio" title="Inicio" onClick={() => navigate('/')}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="m3 10 9-7 9 7M5 9v12h5v-7h4v7h5V9" /></svg></button></>}
      <button
        type="button"
        className="icon-btn tab-add"
        aria-label="Nueva pestaña"
        title="Nueva pestaña (⌘T / Ctrl+T)"
        aria-keyshortcuts="Meta+T Control+T"
        onClick={addTab}
      >
        +
      </button>
      {ventanaMac && <button type="button" className="tabbar-action" aria-label="Abrir asistente IA" title="Asistente IA (⌘⇧K / Ctrl+Shift+K)" onClick={() => window.dispatchEvent(new CustomEvent('bee:asistente', { detail: 'toggle' }))}><IconoAsistente /></button>}
    </div>
  )
}
